import { Container, Box, Grid, Typography, makeStyles } from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import StarIcon from "@material-ui/icons/Star";
import CodeIcon from "@material-ui/icons/Code";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";

import { Loading } from "../../components/shared/loading";
import { HeroParticles } from "../../components/shared/HeroParticles";
import { Reveal } from "../../components/shared/Reveal";

const JUNK = ["n/a", "na", "-", "none", "null", "nil"];
const cleanSkills = (raw) =>
  (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s && !JUNK.includes(s.toLowerCase()));
// Split the bio: the first sentence becomes the intro (pull quote / hero
// line) and the remainder becomes the story body, so nothing reads twice.
// Skips honorifics and initials ("Dr.", "Mr.", "P.") which are not sentence
// ends. If the split would leave too little body, skip the intro entirely.
const isAbbrevEnd = (prefix) =>
  /\b(?:Dr|Mr|Mrs|Ms|Prof|Er|Jr|Sr|St)$/.test(prefix) ||
  /(?:^|[\s.])[A-Z]$/.test(prefix);
const splitBio = (text) => {
  const t = (text || "").trim();
  let from = 0;
  while (true) {
    const i = t.indexOf(". ", from);
    if (i === -1) break;
    if (isAbbrevEnd(t.slice(0, i))) {
      from = i + 2;
      continue;
    }
    if (t.length - i <= 120 || i > 280) break;
    if (i > 40) {
      return { intro: t.slice(0, i + 1), rest: t.slice(i + 2).trim() };
    }
    from = i + 2;
  }
  return { intro: "", rest: t };
};

const useStyles = makeStyles((theme) => ({
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes orbDrift": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(14px, -12px) scale(1.12)" },
  },
  "@keyframes orbDriftAlt": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(-16px, 10px) scale(1.08)" },
  },
  "@keyframes ringGlow": {
    "0%, 100%": { boxShadow: "0 0 0 8px rgba(242,193,78,0.1), 0 22px 54px rgba(0,0,0,0.55)" },
    "50%": { boxShadow: "0 0 0 14px rgba(242,193,78,0.16), 0 22px 54px rgba(0,0,0,0.55)" },
  },
  "@keyframes tileGlow": {
    "0%, 100%": { boxShadow: "0 0 0 6px rgba(199,125,199,0.1), 0 20px 48px rgba(0,0,0,0.5)" },
    "50%": { boxShadow: "0 0 0 11px rgba(199,125,199,0.16), 0 20px 48px rgba(0,0,0,0.5)" },
  },

  // ── Hero shell ──
  heroSection: {
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
  },
  heroOrb: { position: "absolute", borderRadius: "50%", filter: "blur(60px)", pointerEvents: "none", zIndex: 0 },
  heroOrbOne: {
    width: 320, height: 320, top: "-14%", right: "6%",
    background: "radial-gradient(circle, rgba(199,125,199,0.32), transparent 68%)",
    animation: "$orbDrift 9s ease-in-out infinite",
  },
  heroOrbGold: {
    width: 320, height: 320, top: "-14%", right: "6%",
    background: "radial-gradient(circle, rgba(242,193,78,0.22), transparent 68%)",
    animation: "$orbDrift 9s ease-in-out infinite",
  },
  heroOrbTwo: {
    width: 280, height: 280, bottom: "-22%", left: "2%",
    background: "radial-gradient(circle, rgba(155,59,155,0.3), transparent 68%)",
    animation: "$orbDriftAlt 11s ease-in-out infinite",
  },
  heroInner: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: "44px",
    padding: "56px 24px 76px",
    maxWidth: 880,
    margin: "0 auto",
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) both",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      textAlign: "center",
      gap: "26px",
      padding: "44px 20px 68px",
    },
  },
  heroInfo: { color: "#ffffff", flex: 1 },

  // Portraits
  portraitWrapLeader: { position: "relative", flexShrink: 0 },
  portraitLeader: {
    width: 190, height: 190, borderRadius: "50%", objectFit: "cover", display: "block",
    border: "4px solid transparent",
    backgroundImage:
      "linear-gradient(#1a001a, #1a001a), conic-gradient(#f2c14e, #fbeeb5, #b8860b, #f2c14e)",
    backgroundOrigin: "border-box",
    backgroundClip: "content-box, border-box",
    animation: "$ringGlow 4s ease-in-out infinite",
    [theme.breakpoints.down("sm")]: { width: 150, height: 150 },
  },
  crest: {
    position: "absolute", bottom: 6, right: 6,
    width: 44, height: 44, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg, #fbeeb5, #f2c14e)",
    color: "#3a2a00", border: "2px solid #1a001a",
    boxShadow: "0 8px 20px rgba(242,193,78,0.45)",
    "& svg": { fontSize: 22 },
  },
  portraitWrapDev: { position: "relative", flexShrink: 0 },
  portraitDev: {
    width: 168, height: 168, borderRadius: 30, objectFit: "cover", display: "block",
    border: "3px solid transparent",
    backgroundImage:
      "linear-gradient(#1a001a, #1a001a), linear-gradient(135deg, #c77dc7, #6b1b6b, #c77dc7)",
    backgroundOrigin: "border-box",
    backgroundClip: "content-box, border-box",
    animation: "$tileGlow 4s ease-in-out infinite",
    [theme.breakpoints.down("sm")]: { width: 136, height: 136 },
  },
  crestDev: {
    position: "absolute", bottom: -8, right: -8,
    width: 40, height: 40, borderRadius: 12,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg, #9b3b9b, #6b1b6b)",
    color: "#ffffff", border: "2px solid #1a001a",
    boxShadow: "0 8px 20px rgba(155,59,155,0.5)",
    "& svg": { fontSize: 20 },
  },

  rolePill: {
    display: "inline-flex", alignItems: "center", gap: "8px",
    fontSize: "0.6rem", fontWeight: 700, letterSpacing: "2.4px",
    textTransform: "uppercase", borderRadius: 30, padding: "6px 16px", marginBottom: 16,
  },
  rolePillLeader: {
    color: "#f8e3b0", background: "rgba(242,193,78,0.1)", border: "1px solid rgba(242,193,78,0.4)",
    "&::before": { content: '""', width: 6, height: 6, borderRadius: "50%", background: "#f2c14e", boxShadow: "0 0 10px #f2c14e" },
  },
  rolePillDev: {
    color: "#e0b8e0", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(199,125,199,0.35)",
    "&::before": { content: '""', width: 6, height: 6, borderRadius: "50%", background: "#c77dc7", boxShadow: "0 0 10px #c77dc7" },
  },
  memberName: {
    fontSize: "clamp(1.5rem, 3.6vw, 2.2rem)", fontWeight: 700, letterSpacing: "-0.01em",
    marginBottom: 8, lineHeight: 1.15,
    backgroundImage: "linear-gradient(90deg, #ffffff 0%, #e0b8e0 30%, #c77dc7 50%, #e0b8e0 70%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    backgroundClip: "text", color: "transparent",
  },
  memberNameGold: {
    backgroundImage: "linear-gradient(90deg, #ffffff 0%, #fbeeb5 30%, #f2c14e 50%, #fbeeb5 70%, #ffffff 100%)",
  },
  memberDesig: {
    display: "inline-flex", alignItems: "center", gap: "8px",
    fontSize: "0.85rem", fontWeight: 500, color: "rgba(255,255,255,0.85)", marginBottom: 14,
    "& svg": { fontSize: 16, color: "#c77dc7" },
    [theme.breakpoints.down("sm")]: { justifyContent: "center" },
  },
  memberDesigGold: { "& svg": { color: "#f2c14e" } },
  heroIntro: {
    fontSize: "0.85rem", color: "rgba(255,255,255,0.68)", lineHeight: 1.7, maxWidth: 480,
  },

  // ── Body ──
  pageWrapper: { background: "#f5f3f6", paddingBottom: 52 },
  skillsBar: {
    position: "relative", zIndex: 3,
    display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px",
    width: "fit-content", maxWidth: "calc(100% - 32px)",
    margin: "-28px auto 0", padding: "12px 18px",
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(76,0,76,0.1)", borderRadius: 20,
    boxShadow: "0 18px 44px rgba(26,0,26,0.16)",
  },
  skillChip: {
    fontSize: "0.72rem", fontWeight: 600, color: "#4c004c",
    background: "#f6eef6", border: "1px solid rgba(76,0,76,0.1)",
    borderRadius: 20, padding: "6px 14px", whiteSpace: "nowrap",
  },
  skillChipLeader: {
    background: "linear-gradient(135deg, #fdf6e3, #faeec9)",
    border: "1px solid rgba(226,178,60,0.45)", color: "#7a5a00",
  },

  // Leadership: editorial bio with pull-quote + drop cap
  pullQuote: {
    position: "relative",
    maxWidth: 700, margin: "44px auto 0", padding: "30px 34px",
    background: "linear-gradient(160deg, #2d002d 0%, #1a001a 100%)",
    borderRadius: 18, textAlign: "center",
    boxShadow: "0 16px 44px rgba(76,0,76,0.25)",
    "&::before": {
      content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3,
      background: "linear-gradient(90deg, transparent, #f2c14e, transparent)",
    },
  },
  pullQuoteMark: { color: "rgba(242,193,78,0.5)", fontSize: 30 },
  pullQuoteText: {
    fontSize: "0.95rem", color: "#ffffff", fontStyle: "italic", lineHeight: 1.75,
  },
  bioEditorial: {
    maxWidth: 720, margin: "40px auto 0",
    fontSize: "0.95rem", color: "#3a2f3a", lineHeight: 1.9, whiteSpace: "pre-line",
  },
  goldRule: {
    width: 64, height: 2, borderRadius: 2, margin: "26px auto 0",
    background: "linear-gradient(90deg, transparent, #f2c14e 30%, #fbeeb5 50%, #f2c14e 70%, transparent)",
  },

  // Developer: bio card + toolbox
  bioCard: {
    maxWidth: 720, margin: "40px auto 0",
    background: "#ffffff", borderRadius: 18, padding: "28px 30px",
    borderLeft: "4px solid #9b3b9b",
    boxShadow: "0 10px 38px rgba(76,0,76,0.08)",
    fontSize: "0.92rem", color: "#3a2f3a", lineHeight: 1.85, whiteSpace: "pre-line",
  },
  sectionKicker: {
    fontSize: "0.62rem", fontWeight: 700, color: "#9b3b9b",
    textTransform: "uppercase", letterSpacing: "2.4px",
    textAlign: "center", marginBottom: 8,
  },
  sectionKickerGold: { color: "#b8860b" },
  sectionTitle: {
    fontSize: "1.25rem", fontWeight: 700, color: "#4c004c",
    textAlign: "center", letterSpacing: "-0.01em",
  },
  sectionHead: { margin: "46px auto 22px", maxWidth: 500 },
  toolGrid: {
    display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "10px",
    maxWidth: 640, margin: "0 auto",
  },
  toolTile: {
    display: "inline-flex", alignItems: "center", gap: "8px",
    fontSize: "0.78rem", fontWeight: 600, color: "#ffffff",
    background: "linear-gradient(135deg, #6b1b6b, #9b3b9b)",
    borderRadius: 12, padding: "10px 16px",
    boxShadow: "0 8px 20px rgba(107,27,107,0.25)",
    transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": { transform: "translateY(-3px)" },
    "& svg": { fontSize: 15, opacity: 0.85 },
  },

  // More of the team
  moreRow: {
    display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "18px",
  },
  moreCard: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
    width: 128, padding: "16px 10px 14px",
    background: "#ffffff", borderRadius: 16,
    border: "1px solid rgba(76,0,76,0.07)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
    textDecoration: "none",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&, &:hover, &:focus, &:visited": { textDecoration: "none" },
    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 16px 34px rgba(76,0,76,0.16)" },
  },
  moreAvatar: {
    width: 62, height: 62, borderRadius: "50%", objectFit: "cover",
    border: "2px solid rgba(155,59,155,0.4)",
  },
  moreName: {
    fontSize: "0.72rem", fontWeight: 700, color: "#2d002d",
    textAlign: "center", lineHeight: 1.25,
  },
  moreDesig: {
    fontSize: "0.62rem", color: "#9b3b9b", textAlign: "center", lineHeight: 1.3,
  },

  backRow: { display: "flex", justifyContent: "center", gap: "12px", marginTop: 40, flexWrap: "wrap" },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: "8px",
    fontSize: "0.8rem", fontWeight: 600, color: "#ffffff",
    background: "linear-gradient(135deg, #6b1b6b 0%, #4c004c 100%)",
    borderRadius: 11, padding: "10px 22px", textDecoration: "none",
    boxShadow: "0 10px 26px rgba(76,0,76,0.3)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": { color: "#ffffff", textDecoration: "none" },
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 16px 34px rgba(76,0,76,0.4)" },
    "& svg": { fontSize: 16 },
  },
  ghostBtn: {
    display: "inline-flex", alignItems: "center", gap: "8px",
    fontSize: "0.8rem", fontWeight: 600, color: "#4c004c",
    background: "#ffffff", border: "1px solid rgba(76,0,76,0.2)",
    borderRadius: 11, padding: "10px 22px", textDecoration: "none",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": { color: "#4c004c", textDecoration: "none" },
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 26px rgba(76,0,76,0.14)" },
    "& svg": { fontSize: 16 },
  },
}));

export const TeamMember = () => {
  const classes = useStyles();
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    const fd = new FormData();
    fd.append("id", id);
    Promise.all([
      Axios.post("/api/team_member", fd),
      Axios.post("/api/get_all_team", {}),
    ])
      .then(([res, all]) => {
        if (!active) return;
        if (res.data && res.data.status && res.data.data) {
          const m = res.data.data;
          setMember(m);
          const pool = m.role === 1 ? all.data.team1 : all.data.team2;
          setOthers((pool || []).filter((p) => String(p.id) !== String(id)).slice(0, 6));
        } else {
          setNotFound(true);
        }
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  if (notFound) return <Navigate to="/our-team" replace />;
  if (loading || !member) return <Loading />;

  const isLeader = member.role === 1;
  const skills = cleanSkills(member.skill);
  const { intro, rest: bioBody } = splitBio(member.description);
  const firstName = member.name
    .replace(/^(Dr|Mr|Mrs|Ms|Er|Prof)\.?\s+/i, "")
    .split(" ")[0];

  return (
    <div>
      <Helmet>
        <title>{`${member.name} | ${member.designation} | Arawinz`}</title>
        <meta
          name="description"
          content={`${member.name}, ${member.designation} at Arawinz Soft Solutions.`}
        />
      </Helmet>

      {/* Hero */}
      <Box className={classes.heroSection}>
        <HeroParticles id={`team-${id}-particles`} />
        <Box className={`${classes.heroOrb} ${isLeader ? classes.heroOrbGold : classes.heroOrbOne}`} />
        <Box className={`${classes.heroOrb} ${classes.heroOrbTwo}`} />
        <Box className={classes.heroInner}>
          <Box className={isLeader ? classes.portraitWrapLeader : classes.portraitWrapDev}>
            <img
              src={"/static/team/" + member.image}
              alt={member.name}
              className={isLeader ? classes.portraitLeader : classes.portraitDev}
            />
            <Box className={isLeader ? classes.crest : classes.crestDev}>
              {isLeader ? <StarIcon /> : <CodeIcon />}
            </Box>
          </Box>
          <Box className={classes.heroInfo}>
            <span className={`${classes.rolePill} ${isLeader ? classes.rolePillLeader : classes.rolePillDev}`}>
              {isLeader ? "Leadership Team" : "Core Developer"}
            </span>
            <Typography className={`${classes.memberName} ${isLeader ? classes.memberNameGold : ""}`}>
              {member.name}
            </Typography>
            <Typography className={`${classes.memberDesig} ${isLeader ? classes.memberDesigGold : ""}`}>
              <WorkOutlineIcon /> {member.designation}
            </Typography>
            {!isLeader && intro && (
              <Typography className={classes.heroIntro}>{intro}</Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Body */}
      <Box className={classes.pageWrapper}>
        {skills.length > 0 && (
          <Box className={classes.skillsBar}>
            {skills.map((s, i) => (
              <span key={i} className={`${classes.skillChip} ${isLeader ? classes.skillChipLeader : ""}`}>
                {s}
              </span>
            ))}
          </Box>
        )}

        <Container maxWidth="md">
          {isLeader ? (
            <>
              {intro && (
                <Reveal>
                  <Box className={classes.pullQuote}>
                    <FormatQuoteIcon className={classes.pullQuoteMark} />
                    <Typography className={classes.pullQuoteText}>{intro}</Typography>
                  </Box>
                </Reveal>
              )}
              <Reveal>
                <Box>
                  <Box className={classes.sectionHead} style={{ marginBottom: 0 }}>
                    <Typography className={`${classes.sectionKicker} ${classes.sectionKickerGold}`}>
                      Executive Profile
                    </Typography>
                    <Typography className={classes.sectionTitle}>
                      The story so far
                    </Typography>
                  </Box>
                  <Typography className={classes.bioEditorial}>
                    {bioBody}
                  </Typography>
                  <Box className={classes.goldRule} />
                </Box>
              </Reveal>
            </>
          ) : (
            <>
              <Reveal>
                <Box className={classes.bioCard}>{bioBody}</Box>
              </Reveal>
              {skills.length > 0 && (
                <Reveal>
                  <Box>
                    <Box className={classes.sectionHead}>
                      <Typography className={classes.sectionKicker}>Toolbox</Typography>
                      <Typography className={classes.sectionTitle}>
                        What {firstName} works with
                      </Typography>
                    </Box>
                    <Box className={classes.toolGrid}>
                      {skills.map((s, i) => (
                        <span key={i} className={classes.toolTile}>
                          <CodeIcon /> {s}
                        </span>
                      ))}
                    </Box>
                  </Box>
                </Reveal>
              )}
            </>
          )}

          {/* More of the team */}
          {others.length > 0 && (
            <Reveal>
              <Box>
                <Box className={classes.sectionHead}>
                  <Typography className={`${classes.sectionKicker} ${isLeader ? classes.sectionKickerGold : ""}`}>
                    {isLeader ? "More from the leadership" : "More core developers"}
                  </Typography>
                  <Typography className={classes.sectionTitle}>
                    People {firstName} works with
                  </Typography>
                </Box>
                <Box className={classes.moreRow}>
                  {others.map((p) => (
                    <Link to={`/team/${p.id}`} className={classes.moreCard} key={p.id}>
                      <img src={"/static/team/" + p.image} alt={p.name} className={classes.moreAvatar} />
                      <span className={classes.moreName}>{p.name}</span>
                      <span className={classes.moreDesig}>{p.designation}</span>
                    </Link>
                  ))}
                </Box>
              </Box>
            </Reveal>
          )}

          <Reveal>
            <Box className={classes.backRow}>
              <Link to="/our-team" className={classes.ghostBtn}>
                <ArrowBackIcon /> Back to the team
              </Link>
              <Link to="/get-in-touch" className={classes.backBtn}>
                Work with this team <ArrowForwardIcon />
              </Link>
            </Box>
          </Reveal>
        </Container>
      </Box>
    </div>
  );
};
